import type {
  VoiceCall,
  VoiceCallStatus,
  VoiceEligibilityReason,
} from '../services/voice';

/**
 * The vocabulary the call UI is written in.
 *
 * Two things are being described and they must not be confused:
 *
 *   the CALL's status    the server's word for it - RINGING, ACCEPTED, MISSED
 *   this app's PHASE     what is on screen while that is true
 *
 * They are not the same axis. `RINGING` is `ringing-out` for whoever dialled
 * and `incoming` for whoever is being rung; `ACCEPTED` is `connecting` until
 * the audio actually arrives and `connected` after. The status is fact, the
 * phase is presentation, and only the first of the two comes from the server.
 */

/**
 * Where the call UI is.
 *
 *   idle                  nothing on screen
 *   requesting-permission the microphone prompt is up
 *   dialling              `POST /calls` is in flight
 *   ringing-out           we called; the server says it is ringing
 *   incoming              somebody is calling this handset
 *   connecting            answered, audio not yet flowing
 *   connected             audio is flowing
 *   ending                a hang-up is in flight
 *   ended                 over, with a sentence saying how
 */
export type CallPhase =
  | 'idle'
  | 'requesting-permission'
  | 'dialling'
  | 'ringing-out'
  | 'incoming'
  | 'connecting'
  | 'connected'
  | 'ending'
  | 'ended';

/**
 * The phases a call is committed to - anything but "nothing" and "over".
 *
 * What the status poll runs against, what a back press must not dismiss, and
 * what stops a second call being placed on top of the first.
 *
 * `requesting-permission` is in the list and it belongs there: for somebody
 * *answering*, a call is already ringing while the microphone dialog is up,
 * and it can ring out to MISSED behind it. `ended` is out - the call is over,
 * and the card is only still on screen so somebody can read how it went.
 */
export const LIVE_PHASES: CallPhase[] = [
  'requesting-permission',
  'dialling',
  'ringing-out',
  'incoming',
  'connecting',
  'connected',
  'ending',
];

export const isLivePhase = (phase: CallPhase): boolean =>
  LIVE_PHASES.includes(phase);

/** How a finished call is summed up: a heading, and a line under it. */
export type EndedCopy = {
  title: string;
  detail: string | null;
};

/**
 * The sentence for a call that is over.
 *
 * ---------------------------------------------------------------------------
 * WHY MISSED AND REJECTED ARE NOT THE SAME SENTENCE
 * ---------------------------------------------------------------------------
 * The vendor's guide makes the point and it is worth keeping: one means nobody
 * was there, the other means somebody was there and said no. Collapsing them
 * into "call ended" throws away the only fact the customer needs in order to
 * decide what to do next - ring again in a minute, or stop ringing.
 *
 * `role` matters for the same reason. "Declined" is what the caller was told;
 * the person who did the declining does not need telling.
 */
export const endedCopy = (
  status: VoiceCallStatus,
  role: 'caller' | 'callee',
  durationSeconds: number | null,
): EndedCopy => {
  switch (status) {
    case 'COMPLETED':
      return {
        title: 'Call ended',
        detail: durationSeconds != null ? formatDuration(durationSeconds) : null,
      };
    case 'MISSED':
      return role === 'caller'
        ? { title: 'No answer', detail: 'They did not pick up.' }
        : { title: 'Missed call', detail: 'You did not pick up in time.' };
    case 'REJECTED':
      return role === 'caller'
        ? { title: 'Declined', detail: 'They could not take the call.' }
        : { title: 'Call declined', detail: null };
    case 'CANCELLED':
      return role === 'caller'
        ? { title: 'Call cancelled', detail: null }
        : { title: 'Missed call', detail: 'They hung up before you answered.' };
    case 'FAILED':
      return {
        title: 'Call failed',
        detail: 'The call could not be connected. Please try again.',
      };
    default:
      /* RINGING and ACCEPTED never reach here - this is only ever asked about
         a terminal status - but a `default` keeps the switch total rather than
         relying on that staying true. */
      return { title: 'Call ended', detail: null };
  }
};

/**
 * A call that never rang, said the way it actually happened.
 *
 * For `CALLEE_UNREACHABLE`: the server could not reach the other handset at
 * all - no push token on file, or a dead one - so it ended the call rather
 * than playing forty-five seconds of ringback at a phone nobody was ringing.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS IS NOT "NO ANSWER", AND WHY IT NAMES THEM
 * ---------------------------------------------------------------------------
 * "No answer" is a statement about a person: it says they were there and chose
 * not to pick up. Nothing of the sort happened here, and a customer who reads
 * it will ring again in a minute, get the same result, and draw a conclusion
 * about somebody who never knew they were called.
 *
 * The name goes in the title for the same reason: "Ramesh is unavailable right
 * now" is a fact about a situation, where a bare "Unavailable" reads as an
 * error message about the app. The pronoun fallback is there because the name
 * comes from eligibility, which a caller may not have fetched.
 *
 * Deliberately offers nothing to press. A retry cannot work until that handset
 * registers a token again, and a button that is guaranteed to fail is worse
 * than no button.
 */
export const unreachableCopy = (
  name: string | null | undefined,
): EndedCopy => {
  const who = name?.trim();
  return {
    title: who ? `${who} is unavailable right now` : 'They are unavailable right now',
    detail: 'Their phone could not be reached, so the call was not placed.',
  };
};

/** Terminal statuses, i.e. the ones that stop the poll. */
export const isTerminalStatus = (status: VoiceCallStatus): boolean =>
  status !== 'RINGING' && status !== 'ACCEPTED';

/**
 * Seconds as a clock, the way a call log reads it.
 *
 * Minutes and seconds even past an hour - an in-app call between a customer
 * and a professional that runs over sixty minutes is not a case worth a third
 * field, and "72:40" is unambiguous where "1:12:40" invites a misread.
 */
export const formatDuration = (seconds: number): string => {
  const whole = Math.max(0, Math.floor(seconds));
  const minutes = Math.floor(whole / 60);
  const rest = whole % 60;
  return `${minutes}:${String(rest).padStart(2, '0')}`;
};

/**
 * Why the call button is not on offer, in words.
 *
 * Shown beside a disabled control rather than as an alert, so somebody who
 * wonders why it is greyed out has the answer without pressing anything.
 *
 * ---------------------------------------------------------------------------
 * EVERY REASON GETS A SENTENCE, INCLUDING THE LAST ONE
 * ---------------------------------------------------------------------------
 * The button stays visible and disabled for all five, rather than disappearing
 * for some of them. A control that vanishes teaches nobody anything: a
 * customer who saw "Call" on their last booking and cannot find it on this one
 * concludes the app is broken, when the truth is that their professional has
 * not been assigned yet. Disabled-with-a-reason is the honest version of
 * absent, and it costs one line of caption.
 *
 * `VOICE_NOT_CONFIGURED` was the exception until the backend landed and is not
 * one any more. It is still the odd one out - it is a fact about the
 * deployment rather than about this booking - but the customer standing in
 * front of a dead button deserves the same courtesy either way.
 */
export const eligibilityCopy = (
  reason: VoiceEligibilityReason | null,
): string | null => {
  switch (reason) {
    case 'NOT_YET_ASSIGNED':
      return 'Calling opens once a professional is assigned.';
    case 'SUBJECT_NOT_LIVE':
      return 'Calling is only available while the job is live.';
    case 'COUNTERPART_UNAVAILABLE':
      return 'They are not reachable right now.';
    case 'YOU_ARE_ON_A_CALL':
      return 'You are already on a call.';
    case 'VOICE_NOT_CONFIGURED':
      return 'In-app calling is not switched on yet.';
    default:
      return null;
  }
};

/**
 * The status line while a call is being set up or is under way.
 *
 * Every one of these is either a fact the server has confirmed or a plain
 * description of what this app is doing right now. None of them claim a
 * connection that has not been reported.
 */
export const phaseCopy = (phase: CallPhase): string => {
  switch (phase) {
    case 'requesting-permission':
      return 'Checking microphone…';
    case 'dialling':
      return 'Calling…';
    case 'ringing-out':
      return 'Ringing…';
    case 'incoming':
      return 'Incoming call';
    case 'connecting':
      return 'Connecting…';
    case 'connected':
      return 'Connected';
    case 'ending':
      return 'Ending…';
    default:
      return '';
  }
};

/** The other person's name, or a stand-in that is never blank. */
export const nameOf = (call: VoiceCall | null): string =>
  call?.counterpart.displayName?.trim() || 'Homingo';
